import { getCurrencyConfig, formatCurrency } from "@/utils/currency";
import { planNamesFor } from "@/config/planNames";

/**
 * Canonical Bidmio subscription prices.
 * Live catalog: https://app.bidmio.{tld}/public/pricing (iframe on /cennik/).
 * This file is the marketing/SEO copy of those amounts.
 * Base price includes 1 seat; per-seat applies from user 2.
 */
export type CanonicalPlanKey = "solo" | "pro" | "developer";
export type PlanKey = CanonicalPlanKey | "business" | "businessMax";
export type PricingCurrency = "EUR" | "DKK" | "CZK";

export interface SeatPrice {
  base: number;
  perSeat: number;
  /** Developer field worker. Empty means every extra person uses perSeat. */
  fieldPerSeat?: number;
}

export const INCLUDED_SEATS = 1;

/** Solo is capped at two seats; base already includes the first. */
export const SOLO_MAX_USERS = 2;

/** Organic Solo CTAs: fill campaign fields only when the visitor has no ad click. */
export const SOLO_REGISTER_QUERY = "utm_campaign=solo&utm_content=solo-plan";

export const ANNUAL_DISCOUNT: Record<PricingCurrency, number> = {
  EUR: 0.34,
  DKK: 0.17,
  CZK: 0.17,
};

const PLAN_PRICES: Record<
  CanonicalPlanKey,
  Record<PricingCurrency, SeatPrice>
> = {
  solo: {
    EUR: { base: 19, perSeat: 19 },
    DKK: { base: 149, perSeat: 149 },
    CZK: { base: 490, perSeat: 490 },
  },
  pro: {
    EUR: { base: 49, perSeat: 19 },
    DKK: { base: 490, perSeat: 149 },
    CZK: { base: 1190, perSeat: 490 },
  },
  developer: {
    EUR: { base: 99, perSeat: 99, fieldPerSeat: 19 },
    DKK: { base: 790, perSeat: 490, fieldPerSeat: 149 },
    CZK: { base: 2400, perSeat: 1990, fieldPerSeat: 490 },
  },
};

export function resolvePlanKey(plan: PlanKey): CanonicalPlanKey {
  if (plan === "business") return "pro";
  if (plan === "businessMax") return "developer";
  return plan;
}

export function getPricingCurrency(language: string): PricingCurrency {
  const code = getCurrencyConfig(language).code;
  if (code === "DKK" || code === "CZK" || code === "EUR") return code;
  return "EUR";
}

export function getSeatPrice(plan: PlanKey, language: string): SeatPrice {
  const canonical = resolvePlanKey(plan);
  return PLAN_PRICES[canonical][getPricingCurrency(language)];
}

/** Base monthly price for 1 included seat. */
export function getPlanPrice(plan: PlanKey, language: string): number {
  return getSeatPrice(plan, language).base;
}

export function getPerSeatPrice(plan: PlanKey, language: string): number {
  return getSeatPrice(plan, language).perSeat;
}

export function calculateTeamPrice(
  plan: PlanKey,
  users: number,
  language: string,
): number {
  let seats = Math.max(INCLUDED_SEATS, Math.floor(users) || INCLUDED_SEATS);
  if (resolvePlanKey(plan) === "solo") seats = Math.min(seats, SOLO_MAX_USERS);
  const { base, perSeat } = getSeatPrice(plan, language);
  return base + Math.max(0, seats - INCLUDED_SEATS) * perSeat;
}

/** Plan name as superadmin publishes it for the language (see config/planNames). */
export function getPlanDisplayName(plan: PlanKey, language: string): string {
  return planNamesFor(language)[resolvePlanKey(plan)];
}

export function formatPlanPrice(
  plan: PlanKey,
  language: string,
  users: number = INCLUDED_SEATS,
): string {
  return formatCurrency(calculateTeamPrice(plan, users, language), language);
}

const MONTHLY_SUFFIX: Record<string, string> = {
  da: "/md.",
  sk: "/mes.",
  cs: "/měs.",
  en: "/mo.",
  de: "/Monat",
};

export function formatMonthlyPlanPrice(
  plan: PlanKey,
  language: string,
  users: number = INCLUDED_SEATS,
): string {
  return `${formatPlanPrice(plan, language, users)}${MONTHLY_SUFFIX[language] || MONTHLY_SUFFIX.en}`;
}

export function formatProTeamTotals(
  language: string,
  userCounts: number[] = [1, 5, 10],
): Record<number, number> {
  return Object.fromEntries(
    userCounts.map((n) => [n, calculateTeamPrice("pro", n, language)]),
  );
}

export function getAnnualDiscountRate(language: string): number {
  return ANNUAL_DISCOUNT[getPricingCurrency(language)];
}

export function getAnnualDiscountPercent(language: string): number {
  return Math.round(getAnnualDiscountRate(language) * 100);
}

export function applyAnnualDiscount(amount: number, language: string): number {
  return Math.round(amount * (1 - getAnnualDiscountRate(language)));
}

export function getBilledAmount(
  amount: number,
  language: string,
  yearly: boolean,
): number {
  return yearly ? applyAnnualDiscount(amount, language) : amount;
}

/**
 * Length of the free trial in days, as set by the app's trial plan (default 14).
 * Copy that names the trial interpolates this instead of hard-coding the number.
 */
export const TRIAL_DAYS = 14;
